import React, {useState, useEffect} from 'react';
import axios from 'axios';
import BackOfficeMenu from '../components/BackOfficeMenu';
import LoadingSpinner from '../../components/LoadingSpinner/LoadingSpinner';
import {usePermissions} from '../../utils/usePermissions';
import './HeroImages.css';

const API_URL = import.meta.env.VITE_API_URL;

const HeroImages = ({onNavigate, onLogout, currentPage}) => {
  const {hasPermission} = usePermissions();
  const [heroImages, setHeroImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingImage, setEditingImage] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    startDate: '',
    endDate: '',
    isDefault: false,
    isActive: true,
    order: 0,
    image: null
  });
  const [previewUrl, setPreviewUrl] = useState('');

  // Redirection si pas admin/moderateur
  useEffect(() => {
    const userType = localStorage.getItem('typeUser');
    if (userType !== 'admin' && userType !== 'moderateur') {
      onNavigate('profile');
      return;
    }

    // Si c'est un modérateur, vérifier explicitement la permission 'dashboard:read'
    if (userType === 'moderateur' && !hasPermission('dashboard', 'read')) {
      onNavigate('dashboard');
      return;
    }
  }, [onNavigate, hasPermission]);

  useEffect(() => {
    fetchHeroImages();
  }, []);

  const fetchHeroImages = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/hero-images`, {
        headers: {Authorization: `Bearer ${token}`}
      });
      setHeroImages(response.data.data);
      setLoading(false);
    } catch (error) {
      console.error('Erreur lors de la récupération des images:', error);
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const {name, value, type, checked} = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData({...formData, image: file});
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewUrl(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const token = localStorage.getItem('token');
      const formDataToSend = new FormData();

      formDataToSend.append('title', formData.title);
      formDataToSend.append('description', formData.description);
      formDataToSend.append('startDate', formData.startDate);
      formDataToSend.append('endDate', formData.endDate);
      formDataToSend.append('isDefault', formData.isDefault);
      formDataToSend.append('isActive', formData.isActive);
      formDataToSend.append('order', formData.order);

      if (formData.image) {
        formDataToSend.append('image', formData.image);
      }

      if (editingImage) {
        await axios.put(
          `${API_URL}/hero-images/${editingImage._id}`,
          formDataToSend,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'multipart/form-data'
            }
          }
        );
        alert('Image mise à jour avec succès!');
      } else {
        await axios.post(
          `${API_URL}/hero-images`,
          formDataToSend,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'multipart/form-data'
            }
          }
        );
        alert('Image créée avec succès!');
      }

      resetForm();
      fetchHeroImages();
    } catch (error) {
      console.error('Erreur lors de l\'enregistrement:', error);
      alert(error.response?.data?.message || 'Erreur lors de l\'enregistrement');
    }
  };

  const handleEdit = (image) => {
    setEditingImage(image);
    setFormData({
      title: image.title || '',
      description: image.description || '',
      startDate: image.startDate ? new Date(image.startDate).toISOString().split('T')[0] : '',
      endDate: image.endDate ? new Date(image.endDate).toISOString().split('T')[0] : '',
      isDefault: image.isDefault || false,
      isActive: image.isActive ?? true,
      order: image.order || 0,
      image: null
    });
    setPreviewUrl(`${API_URL.replace('/api', '')}${image.imageUrl}`);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette image?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/hero-images/${id}`, {
        headers: {Authorization: `Bearer ${token}`}
      });
      alert('Image supprimée avec succès!');
      fetchHeroImages();
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
      alert('Erreur lors de la suppression');
    }
  };

  const handleSetDefault = async (id) => {
    try {
      const token = localStorage.getItem('token');
      await axios.patch(
        `${API_URL}/hero-images/${id}/set-default`,
        {},
        {
          headers: {Authorization: `Bearer ${token}`}
        }
      );
      alert('Image définie comme image par défaut!');
      fetchHeroImages();
    } catch (error) {
      console.error('Erreur:', error);
      alert('Erreur lors de la mise à jour');
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      startDate: '',
      endDate: '',
      isDefault: false,
      isActive: true,
      order: 0,
      image: null
    });
    setPreviewUrl('');
    setEditingImage(null);
    setShowForm(false);
  };

  const isImageActive = (image) => {
    if (!image.isActive) return false;

    const now = new Date();
    if (image.isDefault) return true;

    if (!image.startDate && !image.endDate) return true;

    const start = image.startDate ? new Date(image.startDate) : null;
    const end = image.endDate ? new Date(image.endDate) : null;

    if (start && now < start) return false;
    if (end && now > end) return false;

    return true;
  };

  if (loading) {
    return (
      <div className="dashboard-container">
        <BackOfficeMenu onNavigate={onNavigate} onLogout={onLogout} currentPage={currentPage} />
        <div className="dashboard-content">
          <LoadingSpinner message="Chargement des images hero..." />
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      <BackOfficeMenu onNavigate={onNavigate} onLogout={onLogout} currentPage={currentPage} />

      <div className="dashboard-content">
        <div className="hero-images-container">
          <div className="dashboard-header">
            <div>
              <h1>Gestion des Images Hero</h1>
              <p>Gérez les images de présentation de la page d'accueil</p>
            </div>
            {hasPermission('heroImages', 'create') && (
              <button
                className="btn-primary"
                onClick={() => setShowForm(!showForm)}
              >
                {showForm ? 'Annuler' : 'Ajouter une Image'}
              </button>
            )}
          </div>

          {showForm && (
            <div className="hero-image-form">
              <h2>{editingImage ? 'Modifier l\'Image' : 'Nouvelle Image'}</h2>
              <form onSubmit={handleSubmit}>
                <div className="form-row">
                  <div className="form-group">
                    <label>Titre</label>
                    <input
                      type="text"
                      name="title"
                      value={formData.title}
                      onChange={handleInputChange}
                      placeholder="Titre de l'image"
                    />
                  </div>

                  <div className="form-group">
                    <label>Ordre d'affichage</label>
                    <input
                      type="number"
                      name="order"
                      value={formData.order}
                      onChange={handleInputChange}
                      min="0"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>Description</label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    placeholder="Description de l'image"
                    rows="3"
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Date de début</label>
                    <input
                      type="date"
                      name="startDate"
                      value={formData.startDate}
                      onChange={handleInputChange}
                    />
                    <small>Laisser vide pour une disponibilité immédiate</small>
                  </div>

                  <div className="form-group">
                    <label>Date de fin</label>
                    <input
                      type="date"
                      name="endDate"
                      value={formData.endDate}
                      onChange={handleInputChange}
                    />
                    <small>Laisser vide pour une disponibilité illimitée</small>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group checkbox-group">
                    <label>
                      <input
                        type="checkbox"
                        name="isDefault"
                        checked={formData.isDefault}
                        onChange={handleInputChange}
                      />
                      Image par défaut (toujours affichée)
                    </label>
                  </div>

                  <div className="form-group checkbox-group">
                    <label>
                      <input
                        type="checkbox"
                        name="isActive"
                        checked={formData.isActive}
                        onChange={handleInputChange}
                      />
                      Image active
                    </label>
                  </div>
                </div>

                <div className="form-group">
                  <label>Image {!editingImage && '*'}</label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageChange}
                    required={!editingImage}
                  />
                  {previewUrl && (
                    <div className="image-preview">
                      <img src={previewUrl} alt="Aperçu" />
                    </div>
                  )}
                </div>

                <div className="form-actions">
                  <button type="submit" className="btn-primary">
                    {editingImage ? 'Mettre à jour' : 'Créer'}
                  </button>
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={resetForm}
                  >
                    Annuler
                  </button>
                </div>
              </form>
            </div>
          )}

          <div className="hero-images-list">
            <h2>Images Hero ({heroImages.length})</h2>
            {heroImages.length === 0 ? (
              <p className="no-images">Aucune image hero trouvée</p>
            ) : (
              <div className="images-grid">
                {heroImages.map((image) => (
                  <div
                    key={image._id}
                    className={`image-card ${!isImageActive(image) ? 'inactive' : ''}`}
                  >
                    <div className="image-preview-card">
                      <img
                        src={`${API_URL.replace('/api', '')}${image.imageUrl}`}
                        alt={image.title || 'Hero image'}
                      />
                      {image.isDefault && (
                        <span className="badge badge-default">Par défaut</span>
                      )}
                      {!isImageActive(image) && (
                        <span className="badge badge-inactive">Inactive</span>
                      )}
                    </div>

                    <div className="image-info">
                      <h3>{image.title || 'Sans titre'}</h3>
                      {image.description && <p>{image.description}</p>}

                      <div className="image-details">
                        <div className="detail-row">
                          <strong>Ordre:</strong> {image.order}
                        </div>
                        {image.startDate && (
                          <div className="detail-row">
                            <strong>Début:</strong>{' '}
                            {new Date(image.startDate).toLocaleDateString('fr-FR')}
                          </div>
                        )}
                        {image.endDate && (
                          <div className="detail-row">
                            <strong>Fin:</strong>{' '}
                            {new Date(image.endDate).toLocaleDateString('fr-FR')}
                          </div>
                        )}
                        <div className="detail-row">
                          <strong>Statut:</strong>{' '}
                          {image.isActive ? (
                            <span className="status-active">✓ Active</span>
                          ) : (
                            <span className="status-inactive">✗ Inactive</span>
                          )}
                        </div>
                      </div>

                      <div className="image-actions">
                        {hasPermission('heroImages', 'update') && (
                          <button
                            className="btn-edit"
                            onClick={() => handleEdit(image)}
                          >
                            Modifier
                          </button>
                        )}
                        {!image.isDefault && hasPermission('heroImages', 'update') && (
                          <button
                            className="btn-default"
                            onClick={() => handleSetDefault(image._id)}
                          >
                            Définir par défaut
                          </button>
                        )}
                        {hasPermission('heroImages', 'delete') && (
                          <button
                            className="btn-delete"
                            onClick={() => handleDelete(image._id)}
                          >
                            Supprimer
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default HeroImages;
